import {InterfaceDescriptor} from "@cm/template-nodes"
import type {
    DescriptorProps,
    InterfaceId,
    TypeDescriptors,
    ConfigInfo,
    CurveInfo,
    ImageInfo,
    MaterialInfo,
    MeshInfo,
    NumberInfo,
    StringInfo,
    TemplateInfo,
    TransformInfo,
} from "@cm/template-nodes"
import type {EvaluatedMattressComponentValueOrigin} from "#mattress/nodes/component/mattress-component-types"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"

type MattressComponentInterfaceDescriptorProps<T extends object = {}> = T & {
    isSetByMattressComponent?: boolean
}

export abstract class MattressComponentInterfaceDescriptor<V, T extends object = {}> extends InterfaceDescriptor<
    V,
    MattressComponentInterfaceDescriptorProps<T>,
    EvaluatedMattressComponentValueOrigin
> {
    constructor(props: DescriptorProps<V, MattressComponentInterfaceDescriptorProps<T>, EvaluatedMattressComponentValueOrigin>) {
        super(props)
    }
}

export class MattressCurveInfo extends MattressComponentInterfaceDescriptor<MattressCurveData | null> {
    override clone(id: InterfaceId, isSetByMattressComponent?: boolean) {
        return new MattressCurveInfo({...this.props, id, isSetByMattressComponent}) as this
    }
}

export class MattressReferenceFrameInfo extends MattressComponentInterfaceDescriptor<MattressReferenceFrameData | null> {
    override clone(id: InterfaceId, isSetByMattressComponent?: boolean) {
        return new MattressReferenceFrameInfo({...this.props, id, isSetByMattressComponent}) as this
    }
}

export type MattressInterfaceDescriptor =
    | MattressCurveInfo
    | MattressReferenceFrameInfo
    | NumberInfo
    | MaterialInfo
    | CurveInfo
    | TemplateInfo
    | ImageInfo
    | MeshInfo
    | StringInfo
    | ConfigInfo
    | TransformInfo

function shallowArrayEquals<T>(a: T[] | undefined, b: T[] | undefined): boolean {
    if (a === b) return true
    if (!a || !b) return false
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false
    }
    return true
}

function descriptorDeepCompare(a: InterfaceDescriptor<unknown> | null | undefined, b: InterfaceDescriptor<unknown> | null | undefined): boolean {
    if (!a || !b) return a === b
    const base = a.props.id === b.props.id && a.props.name === b.props.name && a.props.type === b.props.type && a.props.value === b.props.value
    if (!base) return false
    const originMatch = a.props.origin === b.props.origin
    const pathMatch = shallowArrayEquals(a.props.affectedObjectPathParts, b.props.affectedObjectPathParts)
    return originMatch && pathMatch
}

export const MattressTemplateInterfaceDescriptorTypeDescriptor: TypeDescriptors.Desc<InterfaceDescriptor<unknown>> = {
    deepCompare: descriptorDeepCompare,
    deepCopy: (a) => (a?.props ? a.clone(a.props.id) : a),
}

export const MattressComponentInterfaceDescriptorTypeDescriptor: TypeDescriptors.Desc<MattressComponentInterfaceDescriptor<unknown>> = {
    deepCompare: descriptorDeepCompare as (
        a: MattressComponentInterfaceDescriptor<unknown> | null | undefined,
        b: MattressComponentInterfaceDescriptor<unknown> | null | undefined,
    ) => boolean,
    deepCopy: (a) => (a?.props ? a.clone(a.props.id) : a),
}
